import { ComponentProps, memo, useCallback, useEffect, useMemo, useState } from 'react';
import { useTheme } from 'styled-components';

import { Icon } from '../../ui';
import { findDefaultTabIndex } from '../../utils';
import { Tabs } from '../Tabs';
import { Navigation, NavigationProps } from './Navigation';
import { NavItemContentProps, NavItemProps } from './Navigation/Navigation.types';
import {
  StyledCollapseButtonContainer,
  StyledCollapseButtonWrapper,
  StyledCollapseInnerIcon,
  StyledContentContainer,
  StyledContentDrawer,
  StyledContentWrapper,
  StyledWrapper,
  StyledWrapperComponentTab,
} from './ToolBar.style';
import { ToolBarProps } from './ToolBar.types';

export type { ToolBarProps } from './ToolBar.types';

const WrapperComponentTab = memo(function WrapperComponentTab({
  loaded,
  children,
  hasTabs,
}: {
  loaded: boolean;
  children: React.ReactNode;
  hasTabs?: boolean;
}) {
  if (!loaded) return null;

  return (
    <StyledWrapperComponentTab className={`menu-component ${hasTabs ? 'menu-component-tab' : ''}`}>
      {children}
    </StyledWrapperComponentTab>
  );
});

const MenuContent = memo(function MenuContent({
  featuredItem,
  children,
  items,
  activeSection,
  onClickMenuItem,
  variant,
  visible,
  onTabChange,
}: {
  featuredItem: ToolBarProps['featuredItem'];
  children: React.ReactNode;
  items: ToolBarProps['items'];
  onClickMenuItem?: NavigationProps['onClick'];
  activeSection: string[];
  variant: ToolBarProps['variant'];
  visible: boolean;
  onTabChange?: ComponentProps<typeof MenuItem>['onTabChange'];
}) {
  const [componentsMap, setComponentsMap] = useState<Record<string, boolean>>({});

  const isPreloaded = useCallback((type: string) => componentsMap[type] || false, [componentsMap]);

  const menuItems = useMemo<NavItemProps[]>(
    () => [featuredItem, ...items].filter((item): item is NavItemProps => Boolean(item)),
    [featuredItem, items]
  );

  useEffect(() => {
    setComponentsMap((prevMap) => ({
      ...prevMap,
      [activeSection[0] || '']: true,
    }));
  }, [activeSection]);

  return (
    <StyledContentWrapper visible={visible} variant={variant}>
      <StyledContentDrawer visible={visible} variant={variant}>
        {menuItems.map((item: NavItemProps) => {
          return (
            <MenuItem
              key={`menu-item-${item.type}`}
              item={item}
              activeSection={activeSection}
              isPreloaded={isPreloaded}
              componentsMap={componentsMap}
              onClickMenuItem={onClickMenuItem}
              onTabChange={onTabChange}
            />
          );
        })}
      </StyledContentDrawer>
      {children}
    </StyledContentWrapper>
  );
});

export const MenuItem = memo(function MenuItem({
  item,
  activeSection,
  isPreloaded,
  componentsMap,
  onClickMenuItem,
  onTabChange,
}: {
  item: NavItemProps;
  activeSection: string[];
  isPreloaded: (type: string) => boolean;
  componentsMap: Record<string, boolean>;
  onClickMenuItem?: NavigationProps['onClick'];
  onTabChange?: (tab: string) => void;
}) {
  const [activeIndex, setActiveIndex] = useState(() => {
    if (Array.isArray(item.children) && item.tabPriority) {
      return findDefaultTabIndex(item.children, item.tabPriority);
    }

    return 0;
  });

  useEffect(() => {
    if (Array.isArray(item.children) && activeSection) {
      const sectionIndex = item.children?.findIndex?.(({ type }) =>
        activeSection.includes(type || '')
      );

      if (sectionIndex !== -1) {
        setActiveIndex(sectionIndex);
      }
    }
  }, [activeSection, item.children]);

  return (
    <StyledContentContainer
      key={`menu-tab-${item.type}`}
      data-menu-content={item.type}
      active={activeSection[0] === item.type}
    >
      {Array.isArray(item.children) ? (
        <WrapperComponentTab hasTabs={true} loaded={isPreloaded(item.type)}>
          <Tabs
            onTabChange={(index) => {
              setActiveIndex(index);
              onTabChange?.((item.children as NavItemContentProps[])[index].type ?? '');
            }}
            activeIndex={activeIndex}
            items={item.children.map(({ label, component }, index) => {
              const key = `${item.type}-${label.replace(/\W/g, '').toLowerCase}`;

              if (index === 0) componentsMap[key] = true;

              return {
                label: label,
                content: (
                  <WrapperComponentTab loaded={isPreloaded(key)}>{component}</WrapperComponentTab>
                ),
                onClick: () => onClickMenuItem?.(item),
              };
            })}
          />
        </WrapperComponentTab>
      ) : (
        <WrapperComponentTab loaded={isPreloaded(item.type)}>{item.children}</WrapperComponentTab>
      )}
    </StyledContentContainer>
  );
});

export const ToolBar = ({
  activeSection,
  featuredItem,
  items,
  onClick,
  onVisibilityChange,
  variant = 'static',
  visible = false,
  closeable = true,
}: ToolBarProps) => {
  const [isOpen, setIsOpen] = useState(false);
  const [section, setSection] = useState<string[] | null>(activeSection);

  const theme = useTheme();

  const onCollapseButtonClick = useCallback(
    (isVisible: boolean) => {
      setIsOpen((previous) => {
        const remainsVisible = isVisible ?? !previous;

        setTimeout(() => {
          setSection((state) => (remainsVisible ? state : null));
        }, theme.Transition.Duration.Out);

        return remainsVisible;
      });

      onVisibilityChange(isVisible);
    },
    [onVisibilityChange, theme.Transition.Duration.Out]
  );

  useEffect(
    function updateVisibleWhenPropChange() {
      onCollapseButtonClick(visible);
    },
    [visible, onCollapseButtonClick]
  );

  useEffect(
    function updateActiveSectionWhenPropChange() {
      setIsOpen((isVisible) => {
        activeSection && setSection(activeSection);

        return activeSection ? true : isVisible;
      });
    },
    [activeSection]
  );

  const onTabMenuChange = useCallback(
    (tab: string) => {
      if (section !== null) {
        setSection([section[0], tab]);
      }
    },
    [section]
  );

  return (
    <StyledWrapper variant={variant} visible={!!section && isOpen}>
      <Navigation
        featuredItem={featuredItem}
        items={items}
        variant={variant}
        activeItem={activeSection?.[0] || null}
        visible={isOpen}
        onCollapse={() => onCollapseButtonClick(!isOpen)}
        onClick={(item) => {
          onClick?.(item);
          setSection([item.type]);
        }}
      />

      <MenuContent
        visible={!!section && isOpen}
        activeSection={section || []}
        featuredItem={featuredItem}
        items={items}
        variant={variant}
        onTabChange={onTabMenuChange}
        onClickMenuItem={(item) => {
          setSection([item.type]);
        }}
      >
        <StyledCollapseButtonContainer
          variant={variant}
          visible={isOpen && closeable}
          data-action="collapse"
          onClick={() => onCollapseButtonClick(!isOpen)}
        >
          <StyledCollapseButtonWrapper>
            <Icon
              color={theme.Components.Panel.Background}
              name={'menu-colapse---16'}
              size="XXLarge"
            />
          </StyledCollapseButtonWrapper>

          <StyledCollapseInnerIcon>
            <Icon color={theme.Color.Neutrals.Dark} name={'chevron---left'} />
          </StyledCollapseInnerIcon>
        </StyledCollapseButtonContainer>
      </MenuContent>
    </StyledWrapper>
  );
};
